import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getAuthRedirectUrl } from "./authRedirect";
import {
  dashboardErrorEndsSession,
  isDashboardRequestError,
} from "./dashboardErrorLogoutPolicy";
import { getSupabaseClient } from "./supabase";
import { supabaseErrorEndsSession } from "./supabaseErrorLogoutPolicy";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
        administrator: Boolean(sale.administrator),
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

/**
 * Whether the CRM has been bootstrapped with its first user. `undefined` means
 * Supabase could not answer; callers must not read that as "not initialized",
 * because the only remedy for a genuinely uninitialized CRM is the sign-up
 * page, and sending an operator there also signs them out.
 */
export async function getIsInitialized(): Promise<boolean | undefined> {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data, error } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  if (error) return undefined;
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

/**
 * Supabase could not tell us who the operator is. Distinct from "there is no
 * operator": callers must retry instead of treating it as a denial, otherwise
 * one transient 5xx hides every administrator tab (Queue, Feedback, Marketing,
 * Funnel) for the whole navigation access cache window.
 */
export class OperatorLookupUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OperatorLookupUnavailableError";
  }
}

const NO_ROWS = "PGRST116";

const getSale = async () => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();
  if (errorSession) {
    throw new OperatorLookupUnavailableError(errorSession.message);
  }

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null) {
    return undefined;
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select("id, first_name, last_name, avatar, administrator")
    .match({ user_id: dataSession?.session?.user.id })
    .single();
  if (errorSale && errorSale.code !== NO_ROWS) {
    throw new OperatorLookupUnavailableError(errorSale.message);
  }

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
          options: {
            redirectTo: getAuthRedirectUrl(),
          },
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkError: async (error) => {
      // ra-supabase signs out on any 401/403. Neither the CRM's Vercel proxies
      // (a Supabase timeout while verifying the bearer, a missing
      // administrator role) nor PostgREST (an RLS policy or a revoked
      // function) mean a dead session by that status alone, so both only end
      // the session when Supabase itself confirms the browser's session is
      // gone. Otherwise the error stays with the screen that caused it.
      const probeSession = () => getSupabaseClient().auth.getUser();
      const endsSession = isDashboardRequestError(error)
        ? await dashboardErrorEndsSession(error, probeSession)
        : await supabaseErrorEndsSession(error, probeSession);
      // Reject without a payload, as ra-supabase does, so react-admin shows
      // its standard "logged out" notice rather than the request's message.
      if (endsSession) return Promise.reject();
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (isInitialized === false) {
        await getSupabaseClient().auth.signOut();
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      return baseAuthProvider.checkAuth(params);
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (isInitialized === false) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale role
      const role = sale.administrator ? "admin" : "user";
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
